import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mapApiLanguageToI18n, mapI18nLanguageToApiLanguage } from './queries/language.mapper';
import { LANGUAGE_API, URL_PARAM_LANGUAGE } from '../constants/language.constants';

/**
 * Pure function: Removes language parameter from URL params.
 * Follows "Extract Method" refactoring pattern.
 */
function removeLanguageParameter(params: URLSearchParams): URLSearchParams {
  params.delete(URL_PARAM_LANGUAGE);
  return params;
}

/**
 * Pure function: Sets language parameter in URL params.
 * Follows "Extract Method" refactoring pattern.
 */
function setLanguageParameter(params: URLSearchParams, language: string): URLSearchParams {
  params.set(URL_PARAM_LANGUAGE, language);
  return params;
}

/**
 * Custom hook that synchronizes language between URL parameters and i18n state.
 *
 * Features:
 * - Syncs URL language parameter to i18n on mount
 * - Provides a callback to update both i18n and URL when language changes
 * - Uses replaceState to avoid polluting browser history
 *
 * @returns Object with handleLanguageChange callback
 *
 * @example
 * const { handleLanguageChange } = useLanguageSynchronization();
 *
 * <LanguageSelector onLanguageChange={handleLanguageChange} />
 */
export function useLanguageSynchronization() {
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync URL language parameter to i18n on mount
  useEffect(() => {
    const urlLanguage = searchParams.get(URL_PARAM_LANGUAGE);
    const targetLanguage = mapApiLanguageToI18n(urlLanguage);

    if (targetLanguage && i18n.language !== targetLanguage) {
      i18n.changeLanguage(targetLanguage).catch((error) => {
        console.error('Failed to change language from URL parameter:', error);
      });
    }
  }, [searchParams, i18n]);

  /**
   * Updates both the URL parameter and i18n language when user changes language.
   * This callback is memoized to prevent unnecessary re-renders.
   * Follows "Separate Query from Modifier" pattern - handles side effects declaratively.
   */
  const handleLanguageChange = useCallback(
    (language: string) => {
      // First update i18n state, handling errors gracefully
      i18n.changeLanguage(language).catch((error) => {
        console.error('Failed to change language:', error);
        // Note: URL update still proceeds to maintain consistency with user intent
      });

      // Then synchronize URL state
      const updateUrlLanguageParameter = (prev: URLSearchParams) => {
        const newParams = new URLSearchParams(prev);
        const apiLanguage = mapI18nLanguageToApiLanguage(language);
        const isDefaultLanguage = apiLanguage === LANGUAGE_API.NORWEGIAN;

        // Declarative: Norwegian is default, remove explicit parameter
        return isDefaultLanguage
          ? removeLanguageParameter(newParams)
          : setLanguageParameter(newParams, apiLanguage);
      };

      setSearchParams(updateUrlLanguageParameter, { replace: true });
    },
    [i18n, setSearchParams]
  );

  return { handleLanguageChange };
}
